/**
 * One-stop builder for the `file` prop handed to react-pdf's `<Document>`.
 *
 * Why this exists (the bug it fixes): pdf.js TRANSFERS the bytes you give it to
 * its Web Worker (`postMessage` with a transfer list), which DETACHES the
 * underlying `ArrayBuffer` in the main thread. The old call sites built the
 * prop as `{ data: new Uint8Array(bytes) }` — and `new Uint8Array(arrayBuffer)`
 * is a VIEW over the SAME buffer, not a copy. So pdf.js detached the caller's
 * own `bytes` (typically a react-query cache entry). The NEXT read of that
 * detached buffer threw:
 *   • JavaScriptCore (macOS WKWebView / Tauri desktop): "TypeError: Buffer is
 *     already detached"
 *   • V8 (Chrome / web): "Cannot perform Construct on a detached ArrayBuffer"
 * which, with no React error boundary, blanked the whole desktop app. It was
 * macOS-desktop-only because that path remounts the same cached bytes (navigate
 * away & back within the 5-min staleTime) and JSC is the engine there.
 *
 * The fix: hand pdf.js a fresh COPY each time, so it only ever detaches its own
 * throwaway buffer and the caller's `bytes` stay intact and re-renderable.
 *
 * Lives in `@xop/dashboard-shared` so both the Documentation reader
 * (`apps/web/src/pages/documentation/PdfInlinePreview.tsx`) and the walled CRM
 * sub-app's renderer (`apps/web/src/crm/components/pdf-bytes-preview.tsx`) draw
 * on ONE implementation. See `xop_edge_staff_pdf_iframe_hangs_wkwebview` and
 * `docs/documentation.md` → "Inline PDF preview".
 */

/**
 * Thrown by {@link toPdfFileData} when `bytes` is already detached (or empty) —
 * i.e. there is nothing to copy. Callers catch this and degrade to a Download
 * fallback instead of letting the raw engine TypeError blank the app.
 */
export class PdfBytesDetachedError extends Error {
  constructor() {
    super("PDF bytes are detached or empty and cannot be rendered");
    this.name = "PdfBytesDetachedError";
  }
}

/**
 * Build react-pdf's `{ data }` file prop from `bytes`, backed by a fresh COPY
 * so pdf.js can transfer/detach it without touching the source.
 *
 * @throws {PdfBytesDetachedError} if `bytes` is detached or empty.
 */
export function toPdfFileData(bytes: ArrayBuffer): { data: Uint8Array } {
  // A detached ArrayBuffer reports `byteLength === 0` (it does NOT throw on the
  // property access). Checking this BEFORE we touch the bytes means we never
  // hit the raw "Buffer is already detached" TypeError — we surface our own
  // typed error so the component can show the Download fallback. A genuinely
  // empty buffer is equally un-renderable, so it takes the same path.
  if (bytes.byteLength === 0) throw new PdfBytesDetachedError();
  // `ArrayBuffer.prototype.slice` COPIES into a brand-new buffer that pdf.js is
  // free to transfer/detach; the source `bytes` is left fully intact.
  return { data: new Uint8Array(bytes.slice(0)) };
}

/**
 * The Chrome / Google-style **dark mat** behind an inline PDF's white page
 * sheets. A deliberate, theme-INDEPENDENT product color (like the `#fff` sheet
 * itself): white pages read as paper on a dark mat in both light and dark app
 * themes. Shared so the ONE app-wide reader (`components/pdf/pdf-reader.tsx`,
 * `<PdfReader>`) shows the identical viewer everywhere.
 */
export const PDF_VIEWER_MAT_BG = "#3a3d42";

/**
 * Drop shadow that lifts each white sheet off {@link PDF_VIEWER_MAT_BG}. A raw
 * shadow (NOT `theme.shadows`, which is tuned for light surfaces and vanishes on
 * a dark background) — the signature Chrome/Google PDF-reader look.
 */
export const PDF_SHEET_SHADOW = "0 2px 10px rgba(0,0,0,0.45)";

/**
 * The remaining raw viewer-CHROME colors for `<PdfReader>`: the white paper
 * sheet ({@link PDF_CHROME_LIGHT}) and the black base darkening the toolbar
 * ({@link PDF_CHROME_DARK}). They are theme-INDEPENDENT product colors — the
 * reader is a self-contained product surface with its own palette, like a
 * canvas/map fill — and live here (beside the mat + shadow) rather than as raw
 * hex in `src/components`, which the shared-layer token-purity guard
 * (`styles/theme-tokens.test.ts`) forbids. Layer opacity over them with MUI's
 * `alpha()` at the call site (e.g. `alpha(PDF_CHROME_LIGHT, 0.85)` for a muted
 * white icon on the dark toolbar).
 */
export const PDF_CHROME_LIGHT = "#fff";
export const PDF_CHROME_DARK = "#000";
